
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Challenge, SavedPlan } from './types';
import { CalendarCounter, LevelProgress, BadgeCollection, ChallengeCard, Leaderboard } from './GamificationComponents';
import { CalendarView } from './components/CalendarView';
import { Trophy, Target, Award, Zap, TrendingUp, Sparkles, Plus, Utensils, Dumbbell, ChevronRight, Trash2, CheckCircle2, AlertCircle, Flame, Clock, X } from 'lucide-react';
import { subscribeToUserPlans, deleteAIPlan } from './services/planService';
import { getCoreMessage, getDifferentiator } from './constants/coreMessaging';
import { getPromiseProgress } from './services/promiseService';
import { getRitualProgress } from './services/ritualService';
// Daily Core Action is now fully local - no Firestore dependency
import { SupportedLanguage } from './types';
import i18n from './i18n/index';

export const GamificationDashboard = ({ user, authReady, setView }: { user: User, authReady: boolean, setView?: (view: string) => void }) => {
  const { t } = useTranslation();
  const [savedPlans, setSavedPlans] = useState<SavedPlan[]>([]);
  const [promiseProgress, setPromiseProgress] = useState<any>(null);
  const [ritualProgress, setRitualProgress] = useState<any>(null);
  const [actionText, setActionText] = useState('');
  
  // Daily Core Action - Fully local state (no Firestore dependency)
  const today = new Date().toISOString().split('T')[0];
  const [localActionCompleted, setLocalActionCompleted] = useState<boolean>(() => {
    // Initialize from user data if available, otherwise false
    return user.dailyCoreActionCompleted === true && user.dailyCoreActionDate === today;
  });
  const [localActionType, setLocalActionType] = useState<'workout_confirm' | 'meal_mark' | 'reflection' | null>(null);
  const [showMomentumFeedback, setShowMomentumFeedback] = useState(false); // For "Momentum +1" feedback
  const [momentumMessage, setMomentumMessage] = useState<string>(''); // For emotional micro-feedback
  
  const currentLang = (i18n.language || 'en') as SupportedLanguage;
  
useEffect(() => {
  // 🔑 CRITICAL: Block ALL snapshots until auth is ready
  if (!authReady) return;
  
  let unsubscribe: (() => void) | null = null;

  try {
    const result = subscribeToUserPlans(user.id, (plans) => {
      setSavedPlans(plans);
    });

    if (typeof result === 'function') {
      unsubscribe = result;
    } else {
      console.warn('[DASHBOARD] subscribeToUserPlans did not return a function');
    }
  } catch (error) {
    console.error('[DASHBOARD] Error subscribing to user plans:', error);
  }

  return () => {
    if (typeof unsubscribe === 'function') {
      unsubscribe();
    }
  };
}, [user.id]);


  useEffect(() => {
    // Load promise progress
    getPromiseProgress(user).then(setPromiseProgress);
    // Load ritual progress
    const ritual = getRitualProgress(user);
    setRitualProgress(ritual);
  }, [authReady, user]);

  // Handle Daily Core Action - Fully local (prototype mode)
  const handleCompleteDailyAction = (type: 'workout_confirm' | 'meal_mark' | 'reflection') => {
    // Prevent re-clicking if already completed
    if (localActionCompleted) return;
    
    // Immediate UI feedback
    setLocalActionCompleted(true);
    setLocalActionType(type);
    setActionText('');
    
    // Show "Momentum +1" feedback with emotional message
    setShowMomentumFeedback(true);
    setMomentumMessage(getEmotionalFeedback());
    setTimeout(() => {
      setShowMomentumFeedback(false);
      setMomentumMessage('');
    }, 4000); // Hide after 4 seconds (longer for emotional message)
    
    // Store completion in localStorage for streak tracking
    const streakData = {
      lastDate: today,
      streak: (dailyStreak.streak || 0) + 1
    };
    localStorage.setItem(`dailyStreak_${user.id}`, JSON.stringify(streakData));
    
    // Show success feedback
    if (import.meta.env.DEV) {
      console.log('[CORE_ACTION] ✅ Action completed (local mode):', type);
    }
  };

  // User's active challenges - loaded from Firestore (empty by default, users create their own)
  const [activeChallenges, setActiveChallenges] = useState<Challenge[]>([]);

  // Check if action is completed (local state or user data)
  const isActionCompleted = localActionCompleted || (user.dailyCoreActionCompleted === true && user.dailyCoreActionDate === today);

  // Calculate Daily Streak (locale, no Firestore)
  const calculateDailyStreak = (): { streak: number; isActive: boolean; lastActiveDate: string | null } => {
    const lastDate = user.dailyCoreActionDate || null;
    if (!lastDate) {
      return { streak: 0, isActive: false, lastActiveDate: null };
    }

    const lastDateObj = new Date(lastDate);
    const todayObj = new Date(today);
    const yesterdayObj = new Date(todayObj);
    yesterdayObj.setDate(yesterdayObj.getDate() - 1);

    // Check if last action was today or yesterday (streak active)
    const isToday = lastDate === today;
    const isYesterday = lastDate === yesterdayObj.toISOString().split('T')[0];
    const isActive = isToday || isYesterday;

    if (!isActive) {
      return { streak: 0, isActive: false, lastActiveDate: lastDate };
    }

    // Calculate streak by counting consecutive days
    let streak = 1;
    let checkDate = new Date(lastDateObj);
    checkDate.setDate(checkDate.getDate() - 1);

    // For now, use a simple heuristic: if completed today, assume streak of 1
    // In a real implementation, you'd track historical dates
    if (isToday) {
      streak = 1; // Start with 1, can be enhanced with localStorage history
    }

    return { streak, isActive, lastActiveDate: lastDate };
  };

  const dailyStreak = calculateDailyStreak();

  // Tomorrow Hint (reason to return)
  const getTomorrowHint = (): string => {
    const hints = [
      "Domani: +1 Momentum se completi l'azione entro le 10:00",
      "Domani il tuo profilo mostrerà una nuova aura",
      "Domani: un nuovo giorno per costruire",
      "Domani: la costanza diventa visibile",
      "Domani: un altro passo verso qualcosa di più grande"
    ];
    // Use day of year to rotate hints consistently
    const dayOfYear = Math.floor((new Date().getTime() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
    return hints[dayOfYear % hints.length];
  };

  const tomorrowHint = getTomorrowHint();

  // Emotional micro-feedback messages
  const getEmotionalFeedback = (): string => {
    const messages = [
      "Stai costruendo qualcosa.",
      "Questo è come si accumula potere.",
      "Nessuno lo vede. Ma conta.",
      "Ogni giorno conta.",
      "Stai diventando qualcuno.",
      "La disciplina è invisibile finché non lo è più.",
      "Continua così.",
      "Questo momento conta."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Extract calendar logic before JSX to avoid inline IIFE
  // Calculate current month calendar for stamps display
  const now = new Date();
  const currentCalendar = (user.monthlyCalendars || []).find(
    cal => cal.year === now.getFullYear() && cal.month === now.getMonth() + 1 && !cal.archived
  );
  
  // Calculate stamps text for completed action display
  const stampsText = currentCalendar
    ? t('calendar.stamps_this_month', { n: currentCalendar.totalStamps })
    : '';
  
  // Calculate if user has 7+ stamps for social status badge
  const hasTopWeekStatus = currentCalendar && currentCalendar.totalStamps >= 7;

  // First Action Guidance - Check if user needs onboarding
  // Note: We'll check posts via a simple heuristic (user hasn't posted yet)
  // For now, we'll use a conservative check - if they completed daily action, they're engaged
  const hasNoDailyActions = !isActionCompleted && !(user.dailyCoreActionCompleted === true && user.dailyCoreActionDate === today);
  const hasNoChallenges = activeChallenges.length === 0;
  // Show banner if no daily actions completed AND no challenges (conservative check)
  const needsFirstAction = hasNoDailyActions && hasNoChallenges;
  
  // Check if banner was dismissed (local storage)
  const [firstActionBannerDismissed, setFirstActionBannerDismissed] = useState<boolean>(() => {
    const dismissed = localStorage.getItem('firstActionBannerDismissed');
    return dismissed === 'true';
  });

  const handleDismissFirstActionBanner = () => {
    setFirstActionBannerDismissed(true);
    localStorage.setItem('firstActionBannerDismissed', 'true');
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-10 max-w-5xl mx-auto">
      {/* First Action Guidance Banner */}
      {needsFirstAction && !firstActionBannerDismissed && (
        <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-500/30 rounded-[2rem] p-6 flex items-center justify-between gap-4 animate-fadeIn">
          <div className="flex-1">
            <p className="text-sm font-bold text-white mb-1">Your first action unlocks momentum</p>
            <p className="text-xs text-slate-300">Complete today's core action to get started</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                // Scroll to daily action section (it's below)
                const actionSection = document.querySelector('[data-daily-action]');
                if (actionSection) {
                  actionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all whitespace-nowrap"
            >
              Complete Action
            </button>
            <button
              onClick={handleDismissFirstActionBanner}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Core Message - Subtle */}
      <div className="bg-slate-900/30 border border-slate-800 rounded-[2rem] p-6 text-center">
        <p className="text-sm text-slate-400 italic">
          {getCoreMessage(currentLang)}
        </p>
      </div>

      {/* Daily Core Action - IMPOSSIBLE TO IGNORE */}
      <div data-daily-action className={`border-2 rounded-[2.5rem] p-8 relative ${
        isActionCompleted 
          ? 'bg-emerald-500/10 border-emerald-500' 
          : 'bg-orange-500/10 border-orange-500 animate-pulse'
      }`}>
        {/* Momentum Feedback with Emotional Message */}
        {showMomentumFeedback && (
          <div className="absolute top-4 right-4 bg-emerald-500 text-white px-4 py-3 rounded-xl font-black text-sm uppercase animate-bounce z-10 shadow-lg space-y-1">
            <div>Momentum +1</div>
            {momentumMessage && (
              <div className="text-xs font-normal normal-case italic opacity-90">
                {momentumMessage}
              </div>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {isActionCompleted ? (
              <CheckCircle2 className="text-emerald-400" size={24} />
            ) : (
              <AlertCircle className="text-orange-400" size={24} />
            )}
            <div>
              <h3 className="text-xl font-black uppercase">{t('daily_action.title')}</h3>
              <p className="text-xs text-slate-400">{t('daily_action.subtitle')}</p>
            </div>
          </div>
          {isActionCompleted && (
            <span className="text-emerald-400 font-black text-sm uppercase">{t('daily_action.completed')}</span>
          )}
        </div>

        {!isActionCompleted ? (
          <div className="space-y-4">
            {/* Prototype Mode Notice */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-center">
              <p className="text-[10px] text-cyan-400 font-bold uppercase tracking-widest">
                🔬 Prototype Mode - Local Only
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleCompleteDailyAction('workout_confirm')}
                disabled={isActionCompleted}
                className={`p-4 rounded-xl border text-center transition-all ${
                  isActionCompleted
                    ? 'bg-slate-900/50 border-slate-800 opacity-50 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 active:scale-95'
                }`}
              >
                <Dumbbell className="mx-auto mb-2 text-emerald-400" size={20} />
                <span className="text-xs font-black uppercase">{t('daily_action.workout_confirm')}</span>
              </button>
              <button
                onClick={() => handleCompleteDailyAction('meal_mark')}
                disabled={isActionCompleted}
                className={`p-4 rounded-xl border text-center transition-all ${
                  isActionCompleted
                    ? 'bg-slate-900/50 border-slate-800 opacity-50 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 active:scale-95'
                }`}
              >
                <Utensils className="mx-auto mb-2 text-orange-400" size={20} />
                <span className="text-xs font-black uppercase">{t('daily_action.meal_mark')}</span>
              </button>
              <button
                onClick={() => !isActionCompleted && setLocalActionType('reflection')}
                disabled={isActionCompleted}
                className={`p-4 rounded-xl border text-center transition-all ${
                  isActionCompleted
                    ? 'bg-slate-900/50 border-slate-800 opacity-50 cursor-not-allowed'
                    : 'bg-slate-800 border-slate-700 hover:border-cyan-500 hover:bg-cyan-500/10 active:scale-95'
                }`}
              >
                <Target className="mx-auto mb-2 text-cyan-400" size={20} />
                <span className="text-xs font-black uppercase">{t('daily_action.reflection')}</span>
              </button>
            </div>
            {localActionType === 'reflection' && (
              <div className="space-y-2">
                <textarea
                  value={actionText}
                  onChange={e => setActionText(e.target.value)}
                  placeholder={t('daily_action.placeholder')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-white resize-none focus:border-cyan-500 focus:outline-none"
                  rows={3}
                />
                <button
                  onClick={() => handleCompleteDailyAction('reflection')}
                  className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl text-xs font-black uppercase transition-all active:scale-95"
                >
                  {t('daily_action.complete') || 'Complete Reflection'}
                </button>
              </div>
            )}
            <p className="text-[10px] text-orange-400 font-bold uppercase text-center">
              ⚠ {t('daily_action.consequence')}
            </p>
          </div>
        ) : (
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Flame className="text-orange-400" size={20} />
              <span className="text-lg font-black text-orange-400">
                {dailyStreak.streak > 0 ? `${dailyStreak.streak} giorni` : 'Giorno 1'}
              </span>
            </div>
            <p className="text-sm text-slate-300 font-bold">
              {t('daily_action.completed_today') || 'Action completed today!'}
            </p>
            {stampsText && (
              <p className="text-xs text-emerald-400">
                {stampsText}
              </p>
            )}
            {localActionType && (
              <p className="text-[10px] text-slate-500 italic">
                {t('daily_action.type')}: {localActionType.replace('_', ' ')}
              </p>
            )}
            {/* Tomorrow Hint */}
            <div className="mt-4 pt-4 border-t border-slate-800">
              <p className="text-xs text-slate-400 italic">
                {tomorrowHint}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Promise Progress */}
      {promiseProgress && (
        <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-black uppercase">{t('promise.title')}</h3>
            <Clock className="text-cyan-400" size={20} />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-bold text-cyan-400">{promiseProgress.promiseText}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">{t('promise.progress', { completed: promiseProgress.daysCompleted, total: promiseProgress.daysSelected })}</span>
              <span className="text-emerald-400 font-black">{t('promise.countdown', { days: promiseProgress.daysRemaining })}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-cyan-500 transition-all" 
                style={{ width: `${promiseProgress.progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Ritual Progress */}
      {ritualProgress && ritualProgress.isActive && (
        <div className="bg-orange-500/10 border-2 border-orange-500 rounded-[2rem] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Flame className="text-orange-400" size={24} fill="currentColor" />
              <h3 className="text-lg font-black uppercase">{t('ritual.title')}</h3>
            </div>
            <span className="text-orange-400 font-black">{t('ritual.day', { n: ritualProgress.currentDay })}</span>
          </div>
          <p className="text-sm text-slate-300 mb-2">{t('ritual.subtitle')}</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map(day => (
              <div
                key={day}
                className={`flex-1 h-2 rounded-full ${
                  day <= ritualProgress.currentDay ? 'bg-orange-500' : 'bg-slate-800'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hero Profile Section */}
      <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-[3rem] p-8 border border-slate-800 shadow-2xl overflow-hidden">
        <div className="absolute top-0 right-0 p-10 opacity-5 -mr-10 -mt-10"><Sparkles size={200} /></div>
        
        <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
          <div className="relative">
            <div 
              className={`w-32 h-32 rounded-full p-1.5 bg-gradient-to-tr cursor-pointer hover:scale-105 transition-transform ${
                user.level >= 50 ? 'from-cyan-400 to-emerald-500' : 
                user.level >= 30 ? 'from-yellow-400 to-orange-500' : 
                'from-slate-700 to-slate-500'
              }`}
              onClick={() => setView && setView(`profile:${user.id}`)}
              title={t('profile.go_to_profile')}
            >
              <img src={user.avatar} className="w-full h-full rounded-full border-4 border-slate-900 object-cover" />
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white font-black text-sm px-4 py-1 rounded-full shadow-lg">
              {t('profile.level_short')} {user.level}
            </div>
          </div>

          <div className="flex-1 text-center md:text-left space-y-4">
            <div>
              <h1 className="text-3xl font-black mb-1 flex items-center justify-center md:justify-start gap-3">
                {user.name} 
                {user.level >= 50 && <Award className="text-emerald-400" />}
              </h1>
              <p className="text-emerald-500 font-black text-xs uppercase tracking-widest">{user.title || t('profile.title_default')}</p>
            </div>
            
            <LevelProgress xp={user.xp} level={user.level} t={t} />
            
            <div className="flex flex-wrap justify-center md:justify-start gap-4">
              <CalendarCounter user={user} />
              {/* Social Status Labels */}
              {user.level >= 30 && (
                <div className="flex items-center gap-2 bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-2xl border border-cyan-500/20">
                  <Award size={18} />
                  <span className="font-black text-sm uppercase">{t('social_status.discipline_high')}</span>
                </div>
              )}
              {hasTopWeekStatus && (
                <div className="flex items-center gap-2 bg-orange-500/10 text-orange-400 px-4 py-2 rounded-2xl border border-orange-500/20">
                  <Flame size={18} fill="currentColor" />
                  <span className="font-black text-sm uppercase">{t('social_status.top_week', { percent: '10' })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Calendar - Prominent Display */}
      <section className="space-y-6">
        <h2 className="text-xl font-black flex items-center gap-3 px-2 uppercase italic tracking-tighter">
          <Clock className="text-emerald-400" /> {t('calendar.monthly_calendar')}
        </h2>
        <CalendarView user={user} onStampAdded={(xp) => {
          // Optional: Show XP notification
          console.log(`[DASHBOARD] Stamp added, ${xp} XP earned`);
        }} />
      </section>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          
          {/* AI PLANS SECTION */}
          <section className="space-y-6">
            <div className="flex justify-between items-end px-2">
              <h2 className="text-xl font-black flex items-center gap-3 uppercase italic tracking-tighter">
                <Sparkles className="text-emerald-400" /> {t('dashboard.my_plans')}
              </h2>
            </div>
            
            {savedPlans.length === 0 ? (
              <div className="bg-slate-900/30 border-2 border-dashed border-slate-800 rounded-[2.5rem] p-12 text-center">
                <p className="text-slate-500 font-bold italic text-sm mb-4">{t('dashboard.no_saved_plans')}</p>
                <p className="text-[10px] text-slate-600 uppercase tracking-widest">{t('dashboard.ask_ai_coach')}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {savedPlans.map(plan => (
                  <div key={plan.id} className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] hover:border-emerald-500/30 transition-all group">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-xl ${plan.type === 'workout' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-orange-500/10 text-orange-500'}`}>
                        {plan.type === 'workout' ? <Dumbbell size={20}/> : <Utensils size={20}/>}
                      </div>
                      <button onClick={() => deleteAIPlan(user.id, plan.id)} className="p-2 text-slate-700 hover:text-red-500 transition-colors">
                        <Trash2 size={16}/>
                      </button>
                    </div>
                    <h3 className="font-black text-sm mb-1 uppercase tracking-tight">{plan.title}</h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-6">
                      {t('dashboard.created_on')} {new Date(plan.createdAt?.toDate?.() || Date.now()).toLocaleDateString()}
                    </p>
                    <button 
                      onClick={() => {
                        // Show plan content in a modal or navigate to coach view
                        if (setView) {
                          setView('coach');
                          // Store plan ID in sessionStorage for coach view to display
                          sessionStorage.setItem('viewPlanId', plan.id);
                        }
                      }}
                      className="w-full flex items-center justify-between px-5 py-3 bg-slate-800 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black transition-all uppercase tracking-widest"
                    >
                      {t('dashboard.view_plan')} <ChevronRight size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Active Challenges */}
          <section className="space-y-6">
            <div className="flex justify-between items-end px-2">
              <h2 className="text-xl font-black flex items-center gap-3 uppercase italic tracking-tighter"><Zap className="text-yellow-500" /> {t('dashboard.challenges')}</h2>
              <button 
                onClick={() => {
                  alert(t('dashboard.challenges_coming_soon'));
                }}
                className="text-emerald-400 text-xs font-black hover:underline uppercase tracking-widest"
              >
                {t('dashboard.browse_all')}
              </button>
            </div>
            {activeChallenges.length === 0 ? (
              <div className="bg-slate-900/30 border-2 border-dashed border-slate-800 rounded-[2.5rem] p-12 text-center space-y-4">
                <Zap className="text-slate-600 mx-auto" size={48} />
                <p className="text-slate-500 font-bold italic text-sm">{t('dashboard.no_challenges')}</p>
                <p className="text-xs text-slate-600 uppercase tracking-widest">
                  {t('dashboard.challenges_coming_soon') || 'Challenges feature coming soon!'}
                </p>
                <button 
                  onClick={() => {
                    // Show intentional "coming soon" modal instead of alert
                    const message = t('dashboard.challenges_coming_soon') || 'Challenges are coming soon! This feature will allow you to create and track custom challenges.';
                    alert(message);
                  }}
                  disabled
                  className="px-6 py-3 bg-slate-800 text-slate-500 rounded-xl font-black uppercase cursor-not-allowed opacity-50"
                  title={t('dashboard.challenges_coming_soon') || 'Coming soon'}
                >
                  {t('dashboard.create_challenge')}
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {activeChallenges.map(c => (
                  <ChallengeCard 
                    key={c.id} 
                    challenge={c} 
                    t={t}
                    onStartChallenge={(challengeId) => {
                      alert(t('common.coming_soon') || 'Challenge tracking coming soon!');
                    }}
                  />
                ))}
                <button 
                  onClick={() => {
                    alert(t('dashboard.challenges_coming_soon') || 'Challenges are coming soon!');
                  }}
                  className="h-full min-h-[200px] border-2 border-dashed border-slate-800 rounded-[2rem] flex flex-col items-center justify-center text-slate-600 hover:text-emerald-500 hover:border-emerald-500/50 transition-all gap-4 bg-slate-900/10"
                  title={t('dashboard.challenges_coming_soon') || 'Coming soon'}
                >
                  <div className="p-4 bg-slate-900 rounded-full"><Plus /></div>
                  <span className="font-black text-xs uppercase tracking-widest">{t('dashboard.create_challenge')}</span>
                </button>
              </div>
            )}
          </section>

          {/* Badges Collection */}
          <section className="space-y-6">
              <h2 className="text-xl font-black flex items-center gap-3 px-2 uppercase italic tracking-tighter"><Award className="text-emerald-400" /> {t('dashboard.badge_collection')}</h2>
            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-[3rem]">
              <BadgeCollection unlockedBadges={user.badges || []} />
            </div>
          </section>
        </div>

        {/* Sidebar: Leaderboard */}
        <div className="space-y-6">
          <h2 className="text-xl font-black flex items-center gap-3 px-2 uppercase italic tracking-tighter"><TrendingUp className="text-cyan-400" /> {t('dashboard.top_performers')}</h2>
          <Leaderboard user={user} authReady={authReady} t={t} />
        </div>
      </div>
    </div>
  );
};
